import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon } from '@plerionhq/design-system';
import { ExternalComponentWrapper } from './ExternalComponentWrapper';

export interface SelectOption {
  value: string;
  label?: string | React.ReactNode;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  name?: string;
  label?: React.ReactNode;
  errorMessage?: string;
  noOptionsMessage?: string;
  // React Hook Form compatibility - flexible types to support register()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (...args: any[]) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur?: (...args: any[]) => void;
  // Styling props
  className?: string;
  'data-testid'?: string;
}

const Select = React.forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      options,
      value,
      placeholder = 'Select an option...',
      disabled = false,
      name,
      label,
      errorMessage,
      noOptionsMessage = 'No options available',
      onChange,
      onBlur,
      className = '',
      'data-testid': dataTestId,
    },
    ref,
  ) => {
    const hasErrors = !!errorMessage;

    const handleValueChange = (newValue: string) => {
      // Support both react-hook-form's register (which expects an event) and direct onChange
      if (onChange) {
        // Create a synthetic event for react-hook-form compatibility
        const syntheticEvent = {
          target: {
            value: newValue,
            name: name || '',
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    };

    const combineTriggerClasses = () => {
      const baseClasses =
        'relative w-full cursor-default rounded-md border border-gray-300 dark:border-gray-600 pl-4 pr-12 text-left shadow-sm focus:outline-none focus:ring-1 sm:text-sm min-h-[38px] text-gray-900 dark:text-gray-100';
      const disabledClasses = disabled
        ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
        : 'bg-white dark:bg-gray-900';
      const errorClasses = hasErrors
        ? 'border-2 border-rose-600 focus:border-rose-400 focus:ring-rose-200'
        : 'focus:border-br-primary focus:ring-br-primary';

      return `${baseClasses} ${errorClasses} ${disabledClasses} ${className}`.trim();
    };

    return (
      <div>
        {/* Hidden input for react-hook-form register() support */}
        <input
          type="hidden"
          ref={ref}
          name={name}
          value={value || ''}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
        />

        <SelectPrimitive.Root
          value={value}
          onValueChange={handleValueChange}
          disabled={disabled}
        >
          {label && (
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {label}
            </label>
          )}
          <div className={label ? 'relative mt-1' : 'relative'}>
            <SelectPrimitive.Trigger
              className={combineTriggerClasses()}
              data-testid={dataTestId}
            >
              <span className="block truncate data-[placeholder]:text-gray-400">
                <SelectPrimitive.Value placeholder={placeholder} />
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4">
                <SelectPrimitive.Icon
                  className={`h-5 w-5 ${value ? 'text-gray-400 dark:text-gray-500' : 'text-darkest-gray'}`}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </SelectPrimitive.Icon>
              </span>
            </SelectPrimitive.Trigger>
            <SelectPrimitive.Portal>
              <ExternalComponentWrapper>
                <SelectPrimitive.Content
                  className="relative z-[2147483647] max-h-[var(--radix-select-content-available-height)] w-full min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-md bg-white text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm dark:bg-gray-900 dark:ring-gray-600"
                  position="popper"
                  sideOffset={4}
                >
                  <SelectPrimitive.Viewport className="max-h-[300px] overflow-y-auto p-1">
                    {options.length === 0 ? (
                      <div className="relative cursor-default py-2 pr-9 pl-3 text-gray-500 select-none dark:text-gray-400">
                        {noOptionsMessage}
                      </div>
                    ) : (
                      options.map((option) => (
                        <SelectPrimitive.Item
                          key={option.value}
                          value={option.value}
                          disabled={option.disabled}
                          className="hover:bg-select-hover flex cursor-pointer flex-row py-2 pr-9 pl-3 text-gray-900 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 dark:text-gray-100"
                        >
                          <div className="flex w-full flex-row items-center justify-between">
                            <SelectPrimitive.ItemText className="flex-grow truncate">
                              {option.label || option.value}
                            </SelectPrimitive.ItemText>
                            <SelectPrimitive.ItemIndicator className="text-action-primary">
                              <CheckIcon />
                            </SelectPrimitive.ItemIndicator>
                          </div>
                        </SelectPrimitive.Item>
                      ))
                    )}
                  </SelectPrimitive.Viewport>
                </SelectPrimitive.Content>
              </ExternalComponentWrapper>
            </SelectPrimitive.Portal>
          </div>
        </SelectPrimitive.Root>
        {errorMessage && (
          <p className="mt-2 text-sm text-red-400">{errorMessage}</p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export { Select };
